/**
 * Canonical display label for a business-for-sale listing — the ONE ladder
 * every surface walks: **business name → headline → the listing URL's host →
 * "Untitled listing"**.
 *
 * <p>Before this module the placeholder was minted by roughly a dozen
 * independent literals across these pages, using THREE different ladders: the
 * submission surfaces coalesced `businessName` alone, the listing detail page
 * added `headline`, and the messaging chip resolver added the raw URL but never
 * read `headline` at all. So one listing could read "Untitled listing" on a
 * submission row, "Profitable HVAC contractor…" on its own page, and a
 * 120-character marketplace URL in a chip — three names for one record.</p>
 *
 * <p>The server twin is `PhoenixCapital.Core/Common/ListingLabelHelper.cs`, and
 * it is the one that matters: it composes the buyer intro email's subject line.
 * Nothing imports across the language boundary, so the two are held together by
 * the shared fixture `tests/fixtures/listing-display-name-cases.json`, read by
 * `listingDisplay.test.ts` here and `ListingLabelHelperParityTests.cs` there.</p>
 *
 * <p><b>Prefer the server's answer where there is one.</b> Every listing-bearing
 * read DTO now carries a server-computed `displayName` / `listingDisplayName`,
 * because most client surfaces receive a projection with no `headline` and no
 * `rawUrl` and so cannot walk the ladder at all. Use {@link listingNameOrFallback}
 * on those; {@link listingDisplayName} is for the surfaces that hold the whole
 * listing.</p>
 *
 * <p><b>One ladder, two lengths.</b> {@link listingDisplayName} is the whole name
 * as the marketplace wrote it; {@link listingShortName} is the same ladder with
 * the appended PITCH cut off at the first `|` or em dash. A variant, not a fork
 * — the short one calls the long one — so a rung added here is a rung both
 * lengths get. Constrained slots (page titles, drawer headings, table cells,
 * chips, picker rows) take the short one; prose, anything leaving the app, and
 * every wire DTO take the full one, so the pitch is never actually lost.</p>
 *
 * <p>Mirrors `lib/brokerDisplay.ts` in shape: pure, nullable-safe, never
 * throwing.</p>
 */

/**
 * What a listing is called when NOTHING real is known about it.
 *
 * 🔴 A placeholder, not a name — anything composing prose must use
 * {@link resolveListingName} and branch on null instead of interpolating this.
 * (The client composes no outbound prose today; the server does, which is where
 * that rule is enforced.)
 */
export const UNTITLED_LISTING = "Untitled listing";

/** The three columns the ladder reads. Every field optional: callers hold
 *  different projections and a missing rung is normal, not a data gap. */
export interface ListingNameParts {
  businessName?: string | null;
  headline?: string | null;
  rawUrl?: string | null;
}

/**
 * The listing URL's host — lowercased, `www.` stripped, port and path dropped —
 * or null when the input is blank or is not an http(s) URL.
 *
 * <p>Mirrors `ListingUrlNormalizer.Host` step for step, including the two
 * guards that look defensive and are not: a non-http scheme (`mailto:…` parses
 * perfectly well and is not a listing page) and a userinfo component (a bare
 * email address prepends to `https://agent@example.com`, whose host is
 * `example.com` — every address at that domain would otherwise render as the
 * same listing name).</p>
 *
 * <p>Never throws. The input is free-text paste and a label is not worth an
 * exception.</p>
 */
export function listingUrlHost(rawUrl?: string | null): string | null {
  const trimmed = rawUrl?.trim();
  if (!trimmed) return null;

  // A pasted URL frequently arrives scheme-less ("bizbuysell.com/…"). Prepend
  // rather than reject — it is the single most common paste shape.
  const candidate = hasScheme(trimmed) ? trimmed : `https://${trimmed}`;

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (url.username || url.password) return null;

  // URL already lowercases the hostname; lowercasing again is free and keeps
  // this readable beside the C# twin.
  let host = url.hostname.toLowerCase();
  if (host.startsWith("www.")) host = host.slice(4);
  return host.length === 0 ? null : host;
}

/**
 * Whether the value already declares a URI scheme, per RFC 3986:
 * `ALPHA *( ALPHA / DIGIT / "+" / "-" / "." ) ":"`.
 *
 * <p><b>A dot disqualifies it</b> — the one deliberate departure from the
 * grammar, mirrored from the C# twin. A scheme may legally contain a dot but
 * effectively never does, whereas a scheme-less paste WITH a port
 * (`example.com:8080/listing/1`) looks exactly like a scheme by the letter of
 * the rule and would be read as scheme "example.com" and rejected.</p>
 */
function hasScheme(candidate: string): boolean {
  const colon = candidate.indexOf(":");
  if (colon <= 0) return false;

  const prefix = candidate.slice(0, colon);
  if (!/^[A-Za-z]/.test(prefix)) return false;
  return /^[A-Za-z0-9+-]+$/.test(prefix);
}

/**
 * The ladder, stopping at null when the listing carries nothing real. Callers
 * that render a label want {@link listingDisplayName}; this is for anything that
 * needs to know whether there is a real name at all.
 */
export function resolveListingName(
  listing: ListingNameParts | null | undefined,
): string | null {
  const business = listing?.businessName?.trim();
  if (business) return business;

  const headline = listing?.headline?.trim();
  if (headline) return headline;

  return listingUrlHost(listing?.rawUrl);
}

/** The ladder with {@link UNTITLED_LISTING} at the end — for a surface holding
 *  the listing's own columns. */
export function listingDisplayName(
  listing: ListingNameParts | null | undefined,
): string {
  return resolveListingName(listing) ?? UNTITLED_LISTING;
}

/**
 * The label for a name the SERVER already resolved — a `displayName` /
 * `listingDisplayName` field off the wire.
 *
 * <p><b>`legacyBusinessName` is not belt-and-braces.</b> The DTO fields are
 * OPTIONAL, and the case where one is absent is real: a payload cached before
 * the field shipped (this app's QueryClient holds reads for five minutes and
 * does not refetch on focus). Those payloads still carry `listingBusinessName`,
 * so falling straight through to the placeholder would render a name we HAVE as
 * a name we do not — a regression against the very behaviour this ladder
 * replaced, for as long as the cache lives. Passing it makes the degraded case
 * exactly today's behaviour rather than worse than it.</p>
 *
 * <p>Callers holding the listing's own columns should use
 * {@link listingDisplayName} instead — it can walk the whole ladder rather than
 * just this one rung.</p>
 *
 * <p>Exists so no consumer of a resolved name has to write the placeholder
 * literal itself, which is what `listingDisplayNameSingleSource.test.ts`
 * enforces.</p>
 */
export function listingNameOrFallback(
  name?: string | null,
  legacyBusinessName?: string | null,
): string {
  return resolvedListingName(name, legacyBusinessName) ?? UNTITLED_LISTING;
}

/**
 * The same degradation, stopping at null instead of the placeholder.
 *
 * <p>For surfaces whose fallback is NOT "Untitled listing" — a drawer titled
 * "Listing", a confirm reading "This listing", a wizard that shows nothing until
 * one is picked. Forcing the placeholder into those would put the wrong noun in
 * the chrome; writing `displayName ?? businessName` inline at each of them would
 * put the degradation rule in six places.</p>
 *
 * <p>Every one of those surfaces is chrome, so they all now take
 * {@link resolvedShortListingName} — which is this function's only caller. It is
 * kept rather than inlined so the two lengths stay a symmetric pair: the next
 * surface wanting a nullable name with its pitch intact should find one here
 * instead of re-deriving the degradation rule.</p>
 */
export function resolvedListingName(
  name?: string | null,
  legacyBusinessName?: string | null,
): string | null {
  return name?.trim() || legacyBusinessName?.trim() || null;
}

// ---- The short variant --------------------------------------------------
//
// Same ladder, pitch removed. Four functions mirroring the four above, so a
// caller picks its LENGTH without having to pick a different resolution rule.

/**
 * The characters a marketplace title uses to staple its PITCH onto the end of
 * the business's name: `|` and the EM DASH.
 *
 * <p>Deliberately NOT the en dash `–`. That is a RANGE character —
 * "Phoenix–Tucson", "2019–2024" — and it appears INSIDE names far more often
 * than between a name and a pitch, so cutting on it would mangle honest names
 * to shorten a few. Nor the hyphen, twice over ("250-Terminal", "Wash-N-Go").
 * Mirrors `ListingLabelHelper.PitchSeparators` character for character.</p>
 */
const PITCH_SEPARATOR = /[|—]/;

/**
 * The name with the marketplace pitch cut off — everything up to the first `|`
 * or em dash. Null when the input carries nothing, so it composes with `??`
 * exactly like {@link resolveListingName}.
 *
 * <p><b>What this is for.</b> Listing titles arrive as marketplace copy:
 * "250-Terminal Multi-State ATM Portfolio | $820K Annual Cash Flow". That whole
 * string is right in an email body and wrong in a page title, a drawer heading,
 * a table cell or a chip — where it wrapped a submission's `h1` to five lines of
 * 40px display caps and ran the listing drawer card to about ten. The pitch is
 * not deleted anywhere: {@link listingDisplayName} still yields it and every
 * wire DTO still carries it, so a tooltip or a paragraph can have it back.</p>
 *
 * <p>🔴 <b>Apply it to the NAME, never to a composed label.</b> A submission is
 * titled `${buyer} → ${listing}` and a chip reads `Listing — ${name}`; the
 * second of those composes with an em dash, so shortening the finished string
 * would cut at the SEPARATOR and leave the chip reading "Listing". The call goes
 * inside the interpolation.</p>
 *
 * <p>A name that BEGINS with a separator keeps the whole string rather than
 * becoming empty — a blank label identifies nothing, and a title whose first
 * character is a pipe has no name half to keep.</p>
 */
export function shortenListingName(name?: string | null): string | null {
  const trimmed = name?.trim();
  if (!trimmed) return null;

  const cut = trimmed.search(PITCH_SEPARATOR);
  if (cut < 0) return trimmed;

  const head = trimmed.slice(0, cut).trimEnd();
  return head.length === 0 ? trimmed : head;
}

/**
 * The ladder then the pitch cut off — the short twin of
 * {@link listingDisplayName}, for a surface holding the listing's own columns.
 *
 * <p>Falls back on EXACTLY the same cases as the full one: the shortener never
 * turns a real name into a blank, so it cannot introduce a placeholder the full
 * ladder would not have produced.</p>
 */
export function listingShortName(
  listing: ListingNameParts | null | undefined,
): string {
  return shortenListingName(resolveListingName(listing)) ?? UNTITLED_LISTING;
}

/**
 * The short twin of {@link listingNameOrFallback} — the common one, because most
 * client surfaces hold a server-resolved `listingDisplayName` and no `headline`
 * or `rawUrl` to walk the rest of the ladder with.
 */
export function listingShortNameOrFallback(
  name?: string | null,
  legacyBusinessName?: string | null,
): string {
  return resolvedShortListingName(name, legacyBusinessName) ?? UNTITLED_LISTING;
}

/**
 * The short twin of {@link resolvedListingName} — stopping at null instead of
 * the placeholder, for the drawer titles and confirm sentences whose own
 * fallback is "Listing" or "This listing" rather than "Untitled listing".
 */
export function resolvedShortListingName(
  name?: string | null,
  legacyBusinessName?: string | null,
): string | null {
  return shortenListingName(resolvedListingName(name, legacyBusinessName));
}
